import React, { useEffect, useRef, useState } from "react";
import { Zap, ShieldCheck, Award, Headphones, Truck } from "lucide-react";

const REASONS = [
  { icon: Zap, title: "Service rapide", desc: "Réactivité et exécution sans délai." },
  { icon: Award, title: "Expertise maritime", desc: "Connaissance approfondie du secteur." },
  { icon: Truck, title: "Livraison sécurisée", desc: "Chaîne logistique fiable de bout en bout." },
  { icon: ShieldCheck, title: "Partenaire de confiance", desc: "Engagements respectés, qualité garantie." },
  { icon: Headphones, title: "Assistance dédiée", desc: "Disponibilité et écoute permanentes." },
];

const STATS = [
  { value: 100, suffix: "%", label: "Engagement qualité" },
  { value: 24, suffix: "/7", label: "Disponibilité" },
  { value: 8, suffix: "+", label: "Services offerts" },
  { value: 15, suffix: "+", label: "Partenaires" },
];

function useCounter(target, duration = 1600, start) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

function StatItem({ value, suffix, label, start, idx }) {
  const display = useCounter(value, 1800, start);
  return (
    <div data-testid={`stat-${idx}`}>
      <div
        className="font-heading font-black leading-none tracking-tighter text-[#0A1128]"
        style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
      >
        {display}
        <span className="text-[#D90429]">{suffix}</span>
      </div>
      <div className="mt-2 text-xs sm:text-sm uppercase tracking-[0.25em] text-[#6B7280]">
        {label}
      </div>
    </div>
  );
}

export default function WhyChooseUs() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-[#F3F4F6]"
    >
      <div ref={ref} className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <div className="kms-overline mb-4 flex items-center gap-4">
            <span className="font-heading font-black text-sm text-[#D90429]">05</span>
            <span className="h-px w-12 bg-[#D90429]" />
            <span>Pourquoi nous choisir</span>
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-[#0A1128]">
            Une approche{" "}
            <span className="text-[#D90429]">rigoureuse</span> du service
            maritime.
          </h2>
          <p className="mt-6 text-[#6B7280] text-base sm:text-lg max-w-xl">
            Notre engagement : livrer la bonne ressource, au bon moment, au bon
            port. Avec la précision qu'exige le secteur maritime.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 lg:gap-12 border-y border-gray-300 py-12">
          {STATS.map((s, i) => (
            <StatItem key={s.label} {...s} idx={i} start={inView} />
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {REASONS.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.title}
                className="group p-6 border border-gray-200 bg-white hover:border-[#D90429] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#F3F4F6] border border-gray-200 text-[#0A1128] group-hover:bg-[#D90429] group-hover:text-white group-hover:border-[#D90429] transition-all">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg leading-tight text-[#0A1128]">
                  {r.title}
                </h3>
                <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                  {r.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
